import {
  batchMovePersonToOtherRecord,
  getNestedOrFloatingElementById,
  getNestedSubelementsById,
  getNextArkId,
} from '@fs/zion-sls'

/**
 * Retrieves a list of persons objects from their IDs.
 *
 * @param {Object} params - The parameters object.
 * @param {Array} params.images - The array of images containing person data.
 * @param {Array} params.personsIds - The array of person IDs to retrieve.
 * @returns {Array} The list of persons corresponding to the given IDs.
 */
export const getPersonsFromIds = ({ images, personsIds }) =>
  personsIds.map((id) => getNestedOrFloatingElementById(images, id)).filter(Boolean)

/**
 * Generates an array of dispatch actions to save persons data to a record.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.recordIdParam - The ID of the record or 'new_record' to create a new one.
 * @param {Object|Object[]} params.personsData - The data of the person or an array of persons.
 * @param {string} params.currentPartitionKey - The current partition key.
 * @param {Object[]} params.images - The array of images.
 * @param {string} params.recordType - The type of the record.
 * @returns {Promise<Object[]>} A promise that resolves to an array of dispatch actions.
 */
export const generateSaveDispatches = async ({
  recordIdParam,
  personsData,
  currentPartitionKey,
  images,
  recordType,
}) => {
  let recordId = recordIdParam
  let createNewRecordDispatches = []

  // If it's a single person, put it into an array
  const persons = Array.isArray(personsData) ? personsData : [personsData]

  // We only need to create the new record once if the recordIdParam is 'new_record'
  if (recordIdParam === 'new_record') {
    recordId = await getNextArkId('record')
    createNewRecordDispatches = [
      {
        type: 'CREATE_ELEMENT',
        value: {
          elementType: 'RECORD',
          partitionKey: currentPartitionKey,
          id: recordId,
          extraArgs: recordType !== '' ? { recordType } : {},
        },
      },
      {
        type: 'ADD_SUB',
        value: {
          superPartitionKey: currentPartitionKey,
          subPartitionKey: currentPartitionKey,
          superId: currentPartitionKey,
          subId: recordId,
          elementType: 'IMAGE',
        },
      },
    ]
  }

  // Use reduce to accumulate all dispatches for all persons
  const dispatches = persons.reduce(
    (acc, person) => {
      const sourceRecord = getNestedSubelementsById(images, person?.recordId)?.[0]
      const destinationRecord = getNestedSubelementsById(images, recordId)?.[0]

      // Add move dispatch for the person
      const personMoveDispatches = batchMovePersonToOtherRecord({
        person,
        destRecordId: recordId,
        sourceRecPartitionKey: sourceRecord?.partitionKey,
        destRecPartitionKey: destinationRecord ? destinationRecord.partitionKey : currentPartitionKey,
      })

      // Add the dispatches for this person to the accumulator
      acc.push(...personMoveDispatches)

      return acc
    },
    [...createNewRecordDispatches]
  )

  return dispatches
}
